import React, { useState } from 'react';
import { useController } from 'react-hook-form';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import { useTranslation } from 'src/arb-wrapper';
import { useLazyValidateAliasQuery } from 'src/store/leela-api';
import type { FormRhfControllerProps } from './types';
import { TagCloud } from '../TagCloud';

const ALIAS_REGEX = /^[a-z][a-z0-9-]{0,31}$/;

interface FormSharedUserInputProps extends FormRhfControllerProps {
  required?: boolean;
  currentUser?: string;
  label?: string;
  /** When true, stores the value as alias@amazon.com (validates alias format, appends domain) */
  appendDomain?: boolean;
  /** When true, skips format validation and Brass lookup — accepts any non-empty input (e.g. external emails) */
  skipValidation?: boolean;
  /** Custom placeholder text */
  placeholder?: string;
}

export const FormSharedUserInput: React.FC<FormSharedUserInputProps> = (props) => {
  const { control, name, required = false, currentUser, label: customLabel, appendDomain = false, skipValidation = false, placeholder: customPlaceholder } = props;

  const { t } = useTranslation();
  const { field } = useController({
    control,
    name,
    rules: {
      required: required
        ? { value: true, message: t('form_validation_required_field') }
        : undefined,
    },
  });

  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [triggerValidate] = useLazyValidateAliasQuery();

  const onSubmit = async () => {
    const sanitizedInput = inputValue.trim().toLowerCase();
    setError(null);

    if (!sanitizedInput) return;

    // Format validation
    if (!skipValidation && !ALIAS_REGEX.test(sanitizedInput)) {
      setError(t('shared_user_invalid_alias_format'));
      return;
    }

    // Derive the stored value (alias or alias@amazon.com)
    const storedValue = appendDomain ? `${sanitizedInput}@amazon.com` : sanitizedInput;

    // Duplicate check
    if (field.value?.includes(storedValue)) {
      setError(t('shared_user_duplicate_alias'));
      return;
    }

    // Self-add check
    if (currentUser && sanitizedInput === currentUser.toLowerCase()) {
      setError(t('shared_user_self_add'));
      return;
    }

    // Async validation via Brass (skip for raw email input)
    if (!skipValidation) {
      setIsValidating(true);
      try {
        const result = await triggerValidate(sanitizedInput).unwrap();
        if (!result.valid) {
          setError(t('shared_user_alias_not_found'));
          setIsValidating(false);
          return;
        }
      } catch {
        setError(t('shared_user_validation_error'));
        setIsValidating(false);
        return;
      }
      setIsValidating(false);
    }

    field.onChange(field.value ? [...field.value, storedValue] : [storedValue]);
    setInputValue('');
  };

  return (
    <Column>
      <Row alignmentHorizontal="justify" alignmentVertical="bottom" widths={['fill']}>
        <Input
          data-testid="userId"
          label={customLabel ?? t('program_shared_with_header')}
          placeholder={customPlaceholder ?? t('program_user_alias_placeholder')}
          required={required}
          value={inputValue}
          onChange={(value: string) => {
            setInputValue(value);
            setError(null);
          }}
        />
        <Button data-testid="AddUser" disabled={!inputValue || isValidating} onClick={onSubmit}>
          {isValidating ? <Loader size="small" /> : t('add_user_button')}
        </Button>
      </Row>
      {error && (
        <Alert size="small" type="error">
          {error}
        </Alert>
      )}
      <TagCloud
        tags={field?.value ?? []}
        onClearAllClicked={() => {
          field.onChange([]);
        }}
        onTagClicked={(idx: number) => {
          field.onChange(field.value.toSpliced(idx, 1));
        }}
      />
    </Column>
  );
};
